import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { booksService } from '@/services/books.service';
import { ApiError } from '@/services/http';
import type { BookView } from '@/types/api';

/**
 * AdminAddBookDialog — the "Add Book" affordance in the admin dashboard
 * header. Deliberately minimal per ADMIN_DASHBOARD.md §"Add Book": just a
 * title field → `POST /books` → open `/admin/books/:id` in a new tab so
 * the admin can immediately start filling in the rest of the metadata.
 *
 * Opening the editor in a new tab is a product choice, not a technical
 * one — it preserves the dashboard's filter/pagination URL state so the
 * admin can keep curating after they file the new book.
 */
export interface AdminAddBookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional hook so the dashboard can prepend the freshly-created book to
   * its current list (or trigger a refetch). The component still opens the
   * editor tab regardless — the callback is purely for local-state sync.
   */
  onCreated?: (book: BookView) => void;
}

export function AdminAddBookDialog({ open, onOpenChange, onCreated }: AdminAddBookDialogProps) {
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle('');
    setSubmitting(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error('Please enter a title.');
      return;
    }
    setSubmitting(true);
    try {
      const book = await booksService.create({ title: trimmed });
      toast.success(`Created "${book.title}" — opening editor.`);
      // `noopener` keeps the new tab from sharing a browsing context with
      // the dashboard, and `noreferrer` drops the Referer header (defense
      // in depth — the editor is same-origin anyway).
      window.open(`/admin/books/${book.id}`, '_blank', 'noopener,noreferrer');
      onCreated?.(book);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create the book.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-or-slate-900/55 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-32px)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-or-slate-100 bg-white p-6 shadow-or-pop focus:outline-none">
          <Dialog.Title className="font-display text-[20px] font-bold text-or-slate-900">
            Add a book
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-[13px] text-or-slate-600">
            Start with just the title — you'll fill in the author, cover, labels, and prompts in the
            editor.
          </Dialog.Description>
          <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-5">
            <FormField
              label="Book title"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              maxLength={500}
              placeholder="e.g. The Velvet Tunnel"
              help="Will open in a new tab for editing."
            />
            <div className="flex items-center justify-end gap-2">
              <Dialog.Close asChild>
                <Button variant="secondary" size="md" disabled={submitting}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit" size="md" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create book'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
